import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, fmtDate, type SetCardsResponse, type SetCard } from '../api';
import { useData } from '../App';
import { Empty } from '../ui';
import { guideFor, type GuideSection, type SetGuide } from '../data/setGuides';
import { Mana, withMana } from '../mana';

type Tab = 'guide' | 'cards';
const RARITIES = ['common', 'uncommon', 'rare', 'mythic'] as const;
const RARITY: Record<string, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  mythic: 'Mythic',
};

/* Scryfall prints a straight apostrophe in Warrior's Blades; prose written
   anywhere with smart quotes gets a curly one. Fold them together so a
   reference never dies on a character nobody can see. */
const key = (name: string) => name.toLowerCase().replace(/[\u2018\u2019]/g, "'");

/** One release: the written guide, and every card in the set. */
export default function Utgafa() {
  const { id = '' } = useParams();
  const { sets } = useData();
  const set = sets.find((s) => String(s.id) === id) ?? null;

  const [data, setData] = useState<SetCardsResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<SetCard | null>(null);
  const guide = guideFor(data?.code ?? set?.code);
  const [tab, setTab] = useState<Tab>('guide');

  useEffect(() => {
    let live = true;
    setData(null);
    setFailed(false);
    api
      .setCards(Number(id))
      .then((d) => live && setData(d))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [id]);

  useEffect(() => {
    if (!guide) setTab('cards');
  }, [guide]);

  /* Card names in the guide are resolved against the set itself, so a
     reference can only ever point at a card that is really in it.
     A two-faced card is also filed under its front face, which is what
     anyone writing prose will have typed. */
  const byName = useMemo(() => {
    const m = new Map<string, SetCard>();
    for (const c of data?.cards ?? []) {
      m.set(key(c.name), c);
      const front = c.name.split(' // ')[0];
      if (!m.has(key(front))) m.set(key(front), c);
    }
    return m;
  }, [data]);

  if (!set) {
    return (
      <div className="view">
        <section className="card">
          <Empty title="Útgáfan fannst ekki" body="Hún hefur líklega verið fjarlægð af listanum." />
          <Link className="btn ghost" to="/utgafur" style={{ marginTop: 14 }}>
            Til baka í Útgáfur
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="view">
      <section className="card">
        <div className="card-h">
          <h2>
            {set.name}
            {data?.code ? <span className="lbl" style={{ marginLeft: 10 }}>{data.code}</span> : null}
          </h2>
          <Link className="lbl" to="/utgafur">← Útgáfur</Link>
        </div>

        <div className="set-meta">
          <span>{fmtDate(set.release_date)}</span>
          {data ? <span>{data.cards.length} cards</span> : null}
          {data
            ? RARITIES.map((r) => {
                const n = data.cards.filter((c) => c.rarity === r).length;
                return n ? <span key={r}>{n} {RARITY[r].toLowerCase()}</span> : null;
              })
            : null}
        </div>

        <div className="set-tabs">
          {guide ? (
            <button type="button" className={`filt${tab === 'guide' ? ' on' : ''}`} onClick={() => setTab('guide')}>
              Leiðarvísir
            </button>
          ) : null}
          <button type="button" className={`filt${tab === 'cards' ? ' on' : ''}`} onClick={() => setTab('cards')}>
            Spilin
          </button>
        </div>
      </section>

      {tab === 'guide' && guide ? (
        <section className="card">
          <Guide guide={guide} byName={byName} onOpen={setOpen} />
        </section>
      ) : null}

      {tab === 'cards' ? (
        <section className="card">
          {failed ? (
            <Empty
              title="Náði ekki í spilin"
              body="Scryfall svaraði ekki, eða settið er ekki komið út. Reyndu aftur eftir augnablik."
            />
          ) : !data ? (
            <p className="lbl">Sæki spilin…</p>
          ) : (
            <Cards cards={data.cards} onOpen={setOpen} />
          )}
        </section>
      ) : null}

      {open ? <CardWindow key={open.id} card={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

/* ── the card window ──────────────────────────────────────────────── */

/*
 * A real <dialog>, opened with showModal(), the same as the password dialog.
 *
 * This started as a fixed-position overlay and opened in the middle of the
 * *page* rather than the middle of the window — half a screen down, after a
 * scroll through the gallery. The cause is one line in .view: it carries
 * `animation: rise .3s ease both`, and a fill-mode of `both` keeps the
 * animation's transform applied for good. A retained transform makes the
 * element a containing block, so `position: fixed` inside it resolves against
 * the whole scrolling view instead of the viewport — and .view is also a
 * 12-column grid, so the overlay was being laid out as a grid row too.
 *
 * showModal() puts the dialog in the browser's top layer, which no ancestor
 * can capture, and brings Escape, focus trapping and a locked background with
 * it. Nothing has to be unpicked in .view, whose animation is wanted.
 */
function CardWindow({ card, onClose }: { card: SetCard; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  return (
    <dialog
      className="cw"
      ref={ref}
      onClose={onClose}
      onCancel={onClose}
      aria-label={card.name}
      /* A click on the backdrop reports the dialog itself as its target; the
         padding lives on .cw-in so there is no dead margin that also closes. */
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="cw-in">
        {card.image ? <img src={card.image} alt={card.name} className="cw-img" /> : null}

        <div className="cw-body">
          <div className="cw-h">
            <h3>{card.name}</h3>
            <button type="button" className="cw-x" onClick={onClose} aria-label="Loka">
              ×
            </button>
          </div>

          <p className="cw-cost">
            {card.manaCost ? <span className="cw-mana">{withMana(card.manaCost)}</span> : null}
            {card.typeLine}
          </p>

          {/* Rules text as printed: one paragraph per line break. */}
          {card.oracle
            ? card.oracle.split('\n').map((line, i) =>
                line.trim() === '—' ? (
                  <hr key={i} className="cw-rule" />
                ) : line.trim() === '' ? null : (
                  <p key={i} className="cw-text">
                    {withMana(line)}
                  </p>
                ),
              )
            : null}

          <p className="cw-foot">
            {card.pt ? <b>{card.pt}</b> : null}
            <span>{RARITY[card.rarity] ?? card.rarity}</span>
            <span>#{card.number}</span>
            {card.artist ? <span>{card.artist}</span> : null}
          </p>
        </div>
      </div>
    </dialog>
  );
}

/* ── the guide ────────────────────────────────────────────────────── */

type RefProps = { byName: Map<string, SetCard>; onOpen: (c: SetCard) => void };

/**
 * Guide text with its two markups resolved in one pass: `[[Card Name]]`
 * becomes something you can open, and `{1}{W}` becomes the symbols.
 */
function Rich({ text, byName, onOpen }: { text: string } & RefProps): ReactNode {
  const parts = text.split(/(\[\[[^\]]+\]\]|\{[^}]+\})/g);
  return (
    <>
      {parts.map((part, i) => {
        if (/^\{[^}]+\}$/.test(part)) return <Mana key={i} symbol={part} />;

        const m = /^\[\[([^\]]+)\]\]$/.exec(part);
        if (!m) return part;
        const name = m[1];
        const card = byName.get(key(name));
        // A name the set doesn't have reads as plain text rather than a dead link.
        if (!card) return name;
        return (
          <button key={i} type="button" className="cardref" onClick={() => onOpen(card)}>
            {name}
          </button>
        );
      })}
    </>
  );
}

function Guide({ guide, byName, onOpen }: { guide: SetGuide } & RefProps) {
  const rich = (t: string) => <Rich text={t} byName={byName} onOpen={onOpen} />;

  return (
    <article className="guide">
      <p className="guide-lede">{guide.standfirst}</p>

      {guide.sections.map((s: GuideSection) => (
        <section key={s.id}>
          <h3>{s.heading}</h3>

          {s.body?.map((p, i) => <p key={i}>{rich(p)}</p>)}

          {s.bullets ? (
            <ul className="guide-list">
              {s.bullets.map((b, i) => (
                <li key={i}>
                  {b.lead ? <b>{rich(b.lead)} — </b> : null}
                  {rich(b.text)}
                </li>
              ))}
            </ul>
          ) : null}

          {s.table ? (
            <div className="guide-tw">
              <table className="guide-table">
                <thead>
                  <tr>
                    {s.table.head.map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {s.table.rows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j}>{rich(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {s.callout ? <p className="guide-note">{rich(s.callout)}</p> : null}
        </section>
      ))}

      <p className="guide-source">{guide.sourceNote}</p>
    </article>
  );
}

/* ── the gallery ──────────────────────────────────────────────────── */

function Cards({ cards, onOpen }: { cards: SetCard[]; onOpen: (c: SetCard) => void }) {
  const [q, setQ] = useState('');
  const [rarity, setRarity] = useState<string>('');

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return cards.filter((c) => {
      if (rarity && c.rarity !== rarity) return false;
      if (!needle) return true;
      return (
        c.name.toLowerCase().includes(needle) ||
        c.typeLine.toLowerCase().includes(needle) ||
        c.oracle.toLowerCase().includes(needle)
      );
    });
  }, [cards, q, rarity]);

  return (
    <>
      <div className="filters">
        <input
          type="search"
          placeholder="Search name, type or rules text…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search cards"
        />
        <button type="button" className={`filt${rarity === '' ? ' on' : ''}`} onClick={() => setRarity('')}>
          All
        </button>
        {RARITIES.map((r) => (
          <button
            key={r}
            type="button"
            className={`filt${rarity === r ? ' on' : ''}`}
            onClick={() => setRarity(rarity === r ? '' : r)}
          >
            {RARITY[r]}
          </button>
        ))}
      </div>

      <p className="lbl" style={{ marginTop: 10 }}>
        {visible.length === cards.length ? `${cards.length} cards` : `${visible.length} of ${cards.length} cards`}
      </p>

      {visible.length === 0 ? (
        <Empty title="Nothing found" body="No cards match that search." />
      ) : (
        <div className="set-grid">
          {visible.map((c) => (
            <button key={c.id} type="button" className="set-card" onClick={() => onOpen(c)} title={c.name}>
              {c.image ? (
                <img src={c.image} alt={c.name} loading="lazy" width={244} height={340} />
              ) : (
                <span className="set-card-noimg">{c.name}</span>
              )}
              <span className="set-card-cap">
                <b>{c.number}</b> {c.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
